import {
  CalendarDate,
  calendarDateFromAwsDateTime,
  calendarDateTimeFromTimestamp,
  compareCalendarDates,
  createCalendarDate,
  FeatureAnnouncementType,
  getFeatureAnnouncementTranslationKeys,
  isInstantAfter,
  Language,
  ReleaseNote,
  ReleaseNoteContent,
  ReleaseNoteStatus,
} from '@rentguru/commons-utils';
import { isNil } from 'lodash';
import orderBy from 'lodash/orderBy';

export type AnnouncementEntry = {
  key: string;
  title: string;
  date: CalendarDate | undefined;
  isReleaseNote: boolean;
};

export const ANNOUNCEMENT_TYPE_QUERY_PARAM = 'type';

export const ANNOUNCEMENT_DATES: Partial<Record<FeatureAnnouncementType, CalendarDate>> = {
  [FeatureAnnouncementType.BANK_ACCOUNT_TYPES]: createCalendarDate(2026, 8, 31),
  [FeatureAnnouncementType.CHARGE_MIGRATION]: createCalendarDate(2026, 7, 10),
  [FeatureAnnouncementType.TECHNICS_CERTIFICATES]: createCalendarDate(2026, 9, 21),
};

const ANNOUNCEMENT_TYPES_HIDDEN_FOR_OWNERS: FeatureAnnouncementType[] = [
  FeatureAnnouncementType.BANK_ACCOUNT_TYPES,
  FeatureAnnouncementType.CHARGE_MIGRATION,
  FeatureAnnouncementType.TECHNICS_CERTIFICATES,
];

export const isAnnouncementVisible = (featureAnnouncementType: FeatureAnnouncementType, isOwner: boolean): boolean =>
  !isOwner || !ANNOUNCEMENT_TYPES_HIDDEN_FOR_OWNERS.includes(featureAnnouncementType);

const compareAnnouncementsByMostRecentFirst = (
  featureAnnouncementType: FeatureAnnouncementType,
  otherFeatureAnnouncementType: FeatureAnnouncementType
): number => {
  const date = ANNOUNCEMENT_DATES[featureAnnouncementType];
  const otherDate = ANNOUNCEMENT_DATES[otherFeatureAnnouncementType];
  if (isNil(date) && isNil(otherDate)) {
    return 0;
  }
  if (isNil(date)) {
    return 1;
  }
  if (isNil(otherDate)) {
    return -1;
  }
  return compareCalendarDates(otherDate, date);
};

export const getVisibleAnnouncementTypes = (isOwner: boolean): FeatureAnnouncementType[] =>
  Object.values(FeatureAnnouncementType)
    .filter((featureAnnouncementType) => isAnnouncementVisible(featureAnnouncementType, isOwner))
    .sort(compareAnnouncementsByMostRecentFirst);

export const isReleaseNoteVisible = (releaseNote: ReleaseNote, isOwner: boolean): boolean =>
  releaseNote.status === ReleaseNoteStatus.PUBLISHED && (!isOwner || releaseNote.visibleToOwners);

export const getReleaseNoteContent = (releaseNote: ReleaseNote, language: Language): ReleaseNoteContent | undefined =>
  releaseNote.contents.find((content) => content.language === language);

export const getUnreadReleaseNotes = (
  releaseNotes: ReleaseNote[],
  isOwner: boolean,
  readAt: string | null,
  userCreatedAt: string
): ReleaseNote[] => {
  const watermark = calendarDateTimeFromTimestamp(readAt ?? userCreatedAt);
  return releaseNotes.filter(
    (releaseNote) =>
      isReleaseNoteVisible(releaseNote, isOwner) &&
      !isNil(releaseNote.publishedAt) &&
      isInstantAfter(calendarDateTimeFromTimestamp(releaseNote.publishedAt), watermark)
  );
};

export const getAnnouncementEntries = (
  releaseNotes: ReleaseNote[],
  isOwner: boolean,
  language: Language,
  formatMessage: (id: string) => string
): AnnouncementEntry[] => {
  const releaseNoteEntries = releaseNotes.reduce<AnnouncementEntry[]>((entries, releaseNote) => {
    if (!isReleaseNoteVisible(releaseNote, isOwner)) {
      return entries;
    }
    const content = getReleaseNoteContent(releaseNote, language);
    entries.push({
      key: releaseNote.id,
      title: content?.title ?? '',
      date: isNil(releaseNote.publishedAt) ? undefined : calendarDateFromAwsDateTime(releaseNote.publishedAt),
      isReleaseNote: true,
    });
    return entries;
  }, []);

  const legacyEntries: AnnouncementEntry[] = getVisibleAnnouncementTypes(isOwner).map((featureAnnouncementType) => ({
    key: featureAnnouncementType,
    title: formatMessage(getFeatureAnnouncementTranslationKeys(featureAnnouncementType).title),
    date: ANNOUNCEMENT_DATES[featureAnnouncementType],
    isReleaseNote: false,
  }));

  return orderBy(
    [...releaseNoteEntries, ...legacyEntries],
    [(entry) => isNil(entry.date), (entry) => entry.date?.toString()],
    ['asc', 'desc']
  );
};
